import * as THREE from 'three';
import type { RigCamera } from '../cameraRig';
import { FLOOR_DEPTH_OFFSET } from './depthOffset';
import { axisColors } from './palette';
import type { FloorRect } from './platform';

// A level of the grid (1, 10, 100… units) fades in once its cells are this many
// pixels apart, and takes the major style once they are far enough apart that
// the next level down has started to show between them.
const FADE_IN_PX = 8;
const FULL_PX = 24;
const MAJOR_FROM_PX = 48;
const MAJOR_TO_PX = 160;
// The spacing reported to the user: the finest level at least half faded in.
const LABEL_PX = 16;
const AXIS_WIDTH_PX = 2;
// Fade radius in multiples of the visible half-width where the view meets the
// floor, so the grid runs past the edges of the view and dissolves towards the
// horizon rather than across the part.
const VIEWS_OF_REACH = 5;
const FADE_FROM = 0.55;
// A grazing view meets the floor near the horizon; past this many target
// distances the look point stops following it out.
const MAX_LOOK_RATIO = 4;
// Below any decade the shader is ever asked to draw: no fixed spacing.
const AUTO_LEVEL = -1000;

interface GridStyle {
  minor: number;
  major: number;
  minorAlpha: number;
  majorAlpha: number;
  axisAlpha: number;
}

const LIGHT_STYLE: GridStyle = {
  minor: 0x8a8fa8,
  major: 0x6b7090,
  minorAlpha: 0.28,
  majorAlpha: 0.5,
  axisAlpha: 0.85,
};

const DARK_STYLE: GridStyle = {
  minor: 0x9aa0c0,
  major: 0xb4b9d8,
  minorAlpha: 0.12,
  majorAlpha: 0.26,
  axisAlpha: 0.8,
};

const VERTEX_SHADER = /* glsl */ `
  varying vec3 vWorld;
  void main() {
    vec4 world = modelMatrix * vec4(position, 1.0);
    vWorld = world.xyz;
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 uMinor;
  uniform vec3 uMajor;
  uniform vec3 uAxisX;
  uniform vec3 uAxisY;
  uniform float uMinorAlpha;
  uniform float uMajorAlpha;
  uniform float uAxisAlpha;
  uniform vec2 uCenter;
  uniform float uRadius;
  uniform float uFixedLevel;
  uniform vec4 uHole;
  varying vec3 vWorld;

  float lineCoverage(vec2 p, float spacing, vec2 unitsPerPx, float widthPx) {
    vec2 distPx = abs(fract(p / spacing + 0.5) - 0.5) * spacing / unitsPerPx;
    vec2 cover = clamp(0.5 * widthPx + 0.5 - distPx, 0.0, 1.0);
    return max(cover.x, cover.y);
  }

  float axisCoverage(float d, float unitsPerPx) {
    return clamp(0.5 * ${AXIS_WIDTH_PX.toFixed(2)} + 0.5 - abs(d) / unitsPerPx, 0.0, 1.0);
  }

  void main() {
    vec2 p = vWorld.xy;
    if (all(greaterThan(p, uHole.xy)) && all(lessThan(p, uHole.zw))) discard;
    vec2 unitsPerPx = max(fwidth(p), vec2(1e-6));
    float coarsest = max(unitsPerPx.x, unitsPerPx.y);
    float firstLevel = uFixedLevel > ${AUTO_LEVEL.toFixed(1)}
      ? uFixedLevel
      : ceil(log(coarsest * ${FADE_IN_PX.toFixed(1)}) / log(10.0));

    vec4 grid = vec4(0.0);
    for (int i = 0; i < 3; i++) {
      float spacing = pow(10.0, firstLevel + float(i));
      float cellPx = spacing / coarsest;
      float shown = smoothstep(${FADE_IN_PX.toFixed(1)}, ${FULL_PX.toFixed(1)}, cellPx);
      float major = smoothstep(${MAJOR_FROM_PX.toFixed(1)}, ${MAJOR_TO_PX.toFixed(1)}, cellPx);
      float alpha = lineCoverage(p, spacing, unitsPerPx, mix(1.0, 1.35, major))
        * shown * mix(uMinorAlpha, uMajorAlpha, major);
      if (alpha > grid.a) grid = vec4(mix(uMinor, uMajor, major), alpha);
    }

    grid = mix(grid, vec4(uAxisX, uAxisAlpha), axisCoverage(p.y, unitsPerPx.y));
    grid = mix(grid, vec4(uAxisY, uAxisAlpha), axisCoverage(p.x, unitsPerPx.x));

    float reach = 1.0 - smoothstep(${FADE_FROM.toFixed(2)}, 1.0, distance(p, uCenter) / uRadius);
    float facing = smoothstep(0.0, 0.15, abs(normalize(cameraPosition - vWorld).z));
    grid.a *= reach * facing;
    if (grid.a < 0.003) discard;
    gl_FragColor = grid;
    #include <colorspace_fragment>
  }
`;

function aspectOf(camera: RigCamera): number {
  if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
    const ortho = camera as THREE.OrthographicCamera;
    return (ortho.right - ortho.left) / (ortho.top - ortho.bottom);
  }
  return (camera as THREE.PerspectiveCamera).aspect;
}

function visibleHalfHeight(camera: RigCamera, distance: number): number {
  if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
    const ortho = camera as THREE.OrthographicCamera;
    return (ortho.top - ortho.bottom) / 2 / ortho.zoom;
  }
  const perspective = camera as THREE.PerspectiveCamera;
  return distance * Math.tan((perspective.fov * Math.PI) / 360);
}

export function gridSpacingFor(unitsPerPx: number): number {
  if (!Number.isFinite(unitsPerPx) || unitsPerPx <= 0) return 1;
  return 10 ** Math.ceil(Math.log10(unitsPerPx * LABEL_PX) - 1e-9);
}

export class ViewportGrid {
  readonly mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  private minRadius = 1;
  private reach = 1;
  private depth = 1;
  private readonly forward = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  private fixedSpacing: number | null = null;

  constructor(isDark: boolean) {
    const style = isDark ? DARK_STYLE : LIGHT_STYLE;
    const axes = axisColors(isDark);
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uMinor: { value: new THREE.Color(style.minor) },
        uMajor: { value: new THREE.Color(style.major) },
        uAxisX: { value: new THREE.Color(axes.x) },
        uAxisY: { value: new THREE.Color(axes.y) },
        uMinorAlpha: { value: style.minorAlpha },
        uMajorAlpha: { value: style.majorAlpha },
        uAxisAlpha: { value: style.axisAlpha },
        uCenter: { value: new THREE.Vector2() },
        uRadius: { value: 1 },
        uFixedLevel: { value: AUTO_LEVEL },
        uHole: { value: new THREE.Vector4(0, 0, 0, 0) },
      },
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      polygonOffsetFactor: FLOOR_DEPTH_OFFSET,
      polygonOffsetUnits: FLOOR_DEPTH_OFFSET,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -1;
  }

  /** How far from the camera the grid can be seen, for the far plane. */
  get radius(): number {
    return this.depth;
  }

  place(z: number, minRadius: number): void {
    this.mesh.position.z = z;
    this.minRadius = Math.max(minRadius, 1e-3);
  }

  // Centred where the view axis meets the floor rather than on the orbit
  // target, which a pan or a zoom to the cursor can leave far off the floor.
  update(camera: RigCamera, target: THREE.Vector3): void {
    const toTarget = camera.position.distanceTo(target);
    camera.getWorldDirection(this.forward);
    const along = (this.mesh.position.z - camera.position.z) / this.forward.z;
    const look = along > 0 && Number.isFinite(along) ? Math.min(along, toTarget * MAX_LOOK_RATIO) : toTarget;
    this.look.copy(camera.position).addScaledVector(this.forward, look);
    this.reach = Math.max(
      this.minRadius,
      visibleHalfHeight(camera, look) * Math.max(1, aspectOf(camera)) * VIEWS_OF_REACH,
    );
    this.depth = look + this.reach;
    this.mesh.position.x = this.look.x;
    this.mesh.position.y = this.look.y;
    this.mesh.scale.set(this.reach, this.reach, 1);
    const { material } = this.mesh;
    (material.uniforms.uCenter.value as THREE.Vector2).set(this.look.x, this.look.y);
    material.uniforms.uRadius.value = this.reach;
    // From below, the grid lies in front of the part and is drawn over it.
    material.polygonOffset = camera.position.z > this.mesh.position.z;
  }

  show(shown: boolean): void {
    this.mesh.visible = shown;
  }

  /** Pins the minor spacing in model units; null follows the zoom. */
  setFixedSpacing(spacing: number | null): void {
    this.fixedSpacing = spacing !== null && spacing > 0 ? spacing : null;
    this.mesh.material.uniforms.uFixedLevel.value =
      this.fixedSpacing === null ? AUTO_LEVEL : Math.log10(this.fixedSpacing);
  }

  /** Leaves the floor under `rect` bare, where an opaque plate already covers it. */
  setHole(rect: FloorRect | null): void {
    const hole = this.mesh.material.uniforms.uHole.value as THREE.Vector4;
    if (rect) hole.set(rect.minX, rect.minY, rect.maxX, rect.maxY);
    else hole.set(0, 0, 0, 0);
  }

  /** The minor spacing around the target, as it would be drawn seen face-on. */
  spacingAt(camera: RigCamera, target: THREE.Vector3, viewportHeightPx: number): number {
    if (this.fixedSpacing !== null) return this.fixedSpacing;
    const distance = camera.position.distanceTo(target);
    return gridSpacingFor((2 * visibleHalfHeight(camera, distance)) / Math.max(viewportHeightPx, 1));
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}
